import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowRight, ArrowLeft, Loader2, GraduationCap, User, Users, Phone, Cake, School,
  BookOpen, Compass, Sparkles, CheckCircle2, PartyPopper,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Onboarding — Phase 40.13
// * Viewport-adaptive: multi-step wizard on mobile, single fancy page on desktop.
// * Age-conditional parent block: parent name/mobile required when age < 15.
// * Collects: name, student mobile, DOB, school, class, board, referral, parent
//   fields (conditional).
// ---------------------------------------------------------------------------
const EMPTY_FORM = {
  full_name: '', mobile: '',
  date_of_birth: '',
  school_name: '', class_level: '', board: '',
  referral_source: '',
  parent_name: '', parent_mobile: '',
};

const computeAge = (dob) => {
  if (!dob) return null;
  try {
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  } catch { return null; }
};

const STEPS = [
  { id: 'name',    fields: ['full_name'],                   title: 'What should we call you?',   icon: User,     desc: 'Your full name as it appears in school records.' },
  { id: 'mobile',  fields: ['mobile'],                      title: 'Your mobile number',          icon: Phone,    desc: 'For account recovery + important alerts. No spam.' },
  { id: 'dob',     fields: ['date_of_birth'],               title: 'When were you born?',         icon: Cake,     desc: 'Under 15? We will ask for your parent details too.' },
  { id: 'school',  fields: ['school_name'],                 title: 'Which school do you attend?', icon: School,   desc: 'Your school or institute name.' },
  { id: 'class',   fields: ['class_level', 'board'],        title: 'Class & board',               icon: BookOpen, desc: 'So we can tailor lessons to your syllabus.' },
  { id: 'src',     fields: ['referral_source'],             title: 'Where did you learn about us?', icon: Compass, desc: 'It helps us know what is working.' },
  { id: 'parent',  fields: ['parent_name', 'parent_mobile'], title: 'Parent details',              icon: Users,    desc: 'Required for students under 15.', conditional: true },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [opts, setOpts] = useState({ boards: [], classes: [], referral_sources: [] });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM, full_name: user?.name || '' });
  const [step, setStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const listener = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', listener);
    else mq.addListener(listener);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', listener); else mq.removeListener(listener); };
  }, []);

  const _auth = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    fetch(`${API}/api/onboarding/options`, { credentials: 'include', headers: _auth() })
      .then((r) => r.json()).then(setOpts).catch(() => {});
  }, []);

  const age = useMemo(() => computeAge(form.date_of_birth), [form.date_of_birth]);
  const parentRequired = age !== null && age < 15;

  // Which steps are actually shown given the current form state?
  const activeSteps = useMemo(() => STEPS.filter((s) => !s.conditional || parentRequired), [parentRequired]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = (s) => {
    const errs = [];
    for (const f of s.fields) {
      const val = (form[f] || '').toString().trim();
      if (!val) {
        errs.push(f);
      }
    }
    if (s.id === 'mobile' && form.mobile && !/^[+0-9\s-]{7,15}$/.test(form.mobile.trim())) errs.push('mobile-format');
    if (s.id === 'parent' && parentRequired) {
      if (!form.parent_name?.trim()) errs.push('parent_name');
      if (!form.parent_mobile?.trim()) errs.push('parent_mobile');
    }
    return errs;
  };

  const validateAll = () => {
    const errs = [];
    ['full_name','mobile','date_of_birth','school_name','class_level','board','referral_source'].forEach((f) => {
      if (!form[f]?.toString().trim()) errs.push(f);
    });
    if (parentRequired) {
      if (!form.parent_name?.trim()) errs.push('parent_name');
      if (!form.parent_mobile?.trim()) errs.push('parent_mobile');
    }
    return errs;
  };

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const errs = validateAll();
    if (errs.length) {
      toast.error('Please fill all required fields.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        mobile: form.mobile.trim(),
        parent_name: form.parent_name?.trim() || null,
        parent_mobile: form.parent_mobile?.trim() || null,
        class_level: parseInt(form.class_level, 10),
        board: form.board,
        school_name: form.school_name.trim(),
        date_of_birth: form.date_of_birth,
        referral_source: form.referral_source,
      };
      const r = await fetch(`${API}/api/onboarding/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ..._auth() },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Onboarding failed');
      updateUser?.({
        ...user,
        name: payload.full_name,
        onboarded: true,
        class_level: payload.class_level,
        board: payload.board,
      });
      setDone(true);
      setTimeout(() => navigate('/dashboard'), 1600);
    } catch (err) {
      toast.error(err.message || 'Onboarding failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- Field renderer (shared between wizard step & single-page grid) ----
  const renderField = (fieldName, opts_ = {}) => {
    switch (fieldName) {
      case 'full_name':
        return <Input value={form.full_name} onChange={(e) => setField('full_name', e.target.value)} placeholder="e.g. Aarav Sharma" data-testid="onb-name" autoFocus={opts_.autoFocus} />;
      case 'mobile':
        return <Input inputMode="tel" value={form.mobile} onChange={(e) => setField('mobile', e.target.value)} placeholder="e.g. +91 98765 43210" data-testid="onb-mobile" autoFocus={opts_.autoFocus} />;
      case 'date_of_birth':
        return (
          <>
            <Input type="date" value={form.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} max={new Date().toISOString().slice(0, 10)} data-testid="onb-dob" autoFocus={opts_.autoFocus} />
            {age !== null && (
              <p className={`text-[11px] mt-1 ${parentRequired ? 'text-amber-500' : 'text-emerald-500'}`} data-testid="onb-age-hint">
                {parentRequired
                  ? `You are ${age} — we will collect parent details below.`
                  : `You are ${age} — you can complete onboarding on your own.`}
              </p>
            )}
          </>
        );
      case 'school_name':
        return <Input value={form.school_name} onChange={(e) => setField('school_name', e.target.value)} placeholder="e.g. Delhi Public School, R.K. Puram" data-testid="onb-school" autoFocus={opts_.autoFocus} />;
      case 'class_level':
        return (
          <Select value={form.class_level} onValueChange={(v) => setField('class_level', v)}>
            <SelectTrigger data-testid="onb-class"><SelectValue placeholder="Pick your class" /></SelectTrigger>
            <SelectContent>{opts.classes.map((c) => <SelectItem key={c} value={String(c)}>Class {c}</SelectItem>)}</SelectContent>
          </Select>
        );
      case 'board':
        return (
          <Select value={form.board} onValueChange={(v) => setField('board', v)}>
            <SelectTrigger data-testid="onb-board"><SelectValue placeholder="Pick your board" /></SelectTrigger>
            <SelectContent>{opts.boards.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        );
      case 'referral_source':
        return (
          <Select value={form.referral_source} onValueChange={(v) => setField('referral_source', v)}>
            <SelectTrigger data-testid="onb-referral"><SelectValue placeholder="How did you hear about us?" /></SelectTrigger>
            <SelectContent>{opts.referral_sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        );
      case 'parent_name':
        return <Input value={form.parent_name} onChange={(e) => setField('parent_name', e.target.value)} placeholder="e.g. Ritu Sharma" data-testid="onb-parent-name" />;
      case 'parent_mobile':
        return <Input inputMode="tel" value={form.parent_mobile} onChange={(e) => setField('parent_mobile', e.target.value)} placeholder="e.g. +91 98765 12345" data-testid="onb-parent-mobile" />;
      default: return null;
    }
  };

  const currentStep = activeSteps[step];
  const progressPct = Math.round(((step + 1) / activeSteps.length) * 100);

  const goNext = () => {
    const errs = validateStep(currentStep);
    if (errs.length) { toast.error('Please complete this step first.'); return; }
    if (step + 1 < activeSteps.length) setStep(step + 1);
    else submit();
  };

  // ---- Success splash ----
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500/10 via-background to-emerald-500/10 flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center" data-testid="onb-done">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-2xl">
            <PartyPopper className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-black">All set, {form.full_name.split(' ')[0]}!</h1>
          <p className="text-sm text-muted-foreground mt-1">Your 7-day free trial is now active. Taking you to your dashboard…</p>
        </motion.div>
      </div>
    );
  }

  // ---- Wizard (mobile) ----
  if (isMobile) {
    const Icon = currentStep.icon;
    return (
      <div className="min-h-screen relative overflow-hidden flex flex-col" data-testid="onboarding-wizard">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-200 via-indigo-100 to-fuchsia-100 dark:from-slate-900 dark:via-indigo-950 dark:to-fuchsia-950" />
        <div className="absolute inset-0 pointer-events-none">
          <motion.div initial={{ x: -200 }} animate={{ x: '110vw' }} transition={{ duration: 55, repeat: Infinity, ease: 'linear' }}
            className="absolute top-[10%] text-5xl opacity-70 select-none">☁️</motion.div>
          <motion.div initial={{ x: -250 }} animate={{ x: '110vw' }} transition={{ duration: 80, repeat: Infinity, ease: 'linear', delay: 12 }}
            className="absolute top-[70%] text-4xl opacity-50 select-none">☁️</motion.div>
        </div>
        <div className="relative z-10 p-4 flex-1 flex flex-col">
        {/* Progress bar */}
        <div className="max-w-md mx-auto w-full">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-muted-foreground">Step {step + 1} of {activeSteps.length}</span>
            <span className="font-bold text-indigo-500" data-testid="onb-progress">{progressPct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>

        <div className="flex-1 flex items-center max-w-md mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep.id}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25 }}
              className="w-full"
            >
              <Card className="border-2 border-indigo-200 dark:border-indigo-900 shadow-xl" data-testid="onboarding-card">
                <CardHeader className="pb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center mb-2">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-xl">{currentStep.title}</CardTitle>
                  <p className="text-xs text-muted-foreground">{currentStep.desc}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {currentStep.fields.map((f) => (
                    <div key={f}>
                      <Label className="text-[11px] uppercase font-bold text-muted-foreground mb-1 block">
                        {f === 'full_name' ? 'Full Name' :
                         f === 'mobile' ? 'Mobile Number' :
                         f === 'date_of_birth' ? 'Date of Birth' :
                         f === 'school_name' ? 'School / Institute Name' :
                         f === 'class_level' ? 'Class' :
                         f === 'board' ? 'Board' :
                         f === 'referral_source' ? 'How did you find us?' :
                         f === 'parent_name' ? 'Parent Full Name' :
                         f === 'parent_mobile' ? 'Parent Mobile' : f}
                      </Label>
                      {renderField(f, { autoFocus: currentStep.fields[0] === f })}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Nav buttons */}
              <div className="flex gap-2 mt-4">
                {step > 0 && (
                  <Button type="button" variant="outline" onClick={() => setStep(step - 1)} data-testid="onb-back" className="flex-1">
                    <ArrowLeft className="w-4 h-4 mr-1.5" />Back
                  </Button>
                )}
                <Button type="button" onClick={goNext} disabled={loading} data-testid="onb-next" className="flex-1 bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-600 hover:to-fuchsia-600">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                   step + 1 === activeSteps.length ? <>Finish <Sparkles className="w-4 h-4 ml-1.5" /></> :
                   <>Next <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        </div>
      </div>
    );
  }

  // ---- Single fancy page (desktop) ----
  const gridFields = [
    { key: 'full_name', label: 'Full Name', icon: User },
    { key: 'mobile', label: 'Mobile Number', icon: Phone },
    { key: 'date_of_birth', label: 'Date of Birth', icon: Cake, span: 'md:col-span-1' },
    { key: 'school_name', label: 'School / Institute Name', icon: School, span: 'md:col-span-2' },
    { key: 'class_level', label: 'Class', icon: BookOpen },
    { key: 'board', label: 'Board', icon: GraduationCap },
    { key: 'referral_source', label: 'Where did you hear about us?', icon: Compass, span: 'md:col-span-2' },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="onboarding-page">
      {/* Fancy sky + clouds backdrop — Phase 40.15 */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-200 via-indigo-100 to-fuchsia-100 dark:from-slate-900 dark:via-indigo-950 dark:to-fuchsia-950" />
      <div className="absolute inset-0 pointer-events-none">
        <motion.div initial={{ x: -200 }} animate={{ x: '110vw' }} transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          className="absolute top-[8%] text-6xl opacity-70 select-none">☁️</motion.div>
        <motion.div initial={{ x: -300 }} animate={{ x: '110vw' }} transition={{ duration: 90, repeat: Infinity, ease: 'linear', delay: 8 }}
          className="absolute top-[22%] text-7xl opacity-60 select-none">☁️</motion.div>
        <motion.div initial={{ x: -400 }} animate={{ x: '110vw' }} transition={{ duration: 75, repeat: Infinity, ease: 'linear', delay: 20 }}
          className="absolute top-[55%] text-5xl opacity-50 select-none">☁️</motion.div>
        <motion.div initial={{ x: -250 }} animate={{ x: '110vw' }} transition={{ duration: 110, repeat: Infinity, ease: 'linear', delay: 35 }}
          className="absolute top-[75%] text-6xl opacity-40 select-none">☁️</motion.div>
      </div>
      <div className="relative z-10 min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shadow-lg">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-black">Tell us a bit about you</h1>
          </div>
          <p className="text-sm text-muted-foreground">Takes 60 seconds · unlocks your 7-day free trial</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="border-2 border-indigo-200 dark:border-indigo-900 shadow-2xl" data-testid="onboarding-card">
            <CardContent className="p-6">
              <form onSubmit={submit} className="space-y-6" data-testid="onboarding-form">
                {/* Student block */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                      <User className="w-4 h-4 text-indigo-500" />
                    </div>
                    <h2 className="text-sm font-bold uppercase tracking-wide">Your details</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {gridFields.map((gf) => {
                      const Icon = gf.icon;
                      return (
                        <div key={gf.key} className={gf.span || ''}>
                          <Label className="text-[11px] uppercase font-bold text-muted-foreground mb-1.5 flex items-center gap-1">
                            <Icon className="w-3 h-3" />{gf.label}
                          </Label>
                          {renderField(gf.key)}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Parent block — animates in only when age < 15 */}
                <AnimatePresence>
                  {parentRequired && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                      data-testid="onb-parent-section"
                    >
                      <div className="pt-4 border-t border-dashed border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                            <Users className="w-4 h-4 text-amber-500" />
                          </div>
                          <h2 className="text-sm font-bold uppercase tracking-wide">Parent details</h2>
                          <span className="text-[10px] text-amber-500 font-semibold">Required (under 15)</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-[11px] uppercase font-bold text-muted-foreground mb-1.5 flex items-center gap-1">
                              <Users className="w-3 h-3" />Parent Full Name
                            </Label>
                            {renderField('parent_name')}
                          </div>
                          <div>
                            <Label className="text-[11px] uppercase font-bold text-muted-foreground mb-1.5 flex items-center gap-1">
                              <Phone className="w-3 h-3" />Parent Mobile
                            </Label>
                            {renderField('parent_mobile')}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-600 hover:to-fuchsia-600 shadow-lg" data-testid="onb-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start my 7-day free trial <Sparkles className="w-4 h-4 ml-2" /></>}
                </Button>
                <p className="text-[10px] text-muted-foreground text-center">After 7 days, choose ₹399/month, ₹599 family or ₹4,453/year (7% off) to keep going. All prices include GST.</p>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
      </div>
    </div>
  );
}
